import { useState, useEffect } from 'preact/hooks';
import { authService } from '../functions/auth/authService';
import {
  fetchUnitLocations,
  type UnitLocationRow,
} from '../functions/apis/unitCoverageService';

export interface UseUnitCoverageArgs {
  /** Wait until FilterContext finished loading (client list / selection). */
  filterLoading: boolean;
  /**
   * Selected client’s `user_id_no` (from FilterContext).
   * Used with **admin** + **My units** only (see `showAllRisks`).
   */
  scopeUserId: number | null;
  /**
   * Same meaning as the Geospatial risks **Scope** toggle (**All** vs **My units**).
   * **Admin + All:** no `user_id_no` → global units. **Admin + My units:** `user_id_no` = selected client.
   * **Client + All:** `filter_by_units=false` on the API → global aggregate. **Client + My units:** default scoped units.
   */
  showAllRisks: boolean;
}

export interface UnitCoverageState {
  locations: UnitLocationRow[];
  loading: boolean;
  error: string | null;
}

/**
 * Loads aggregated unit locations for the geospatial coverage map.
 * Refetches when filters finish loading, scope toggle, or selected client changes.
 */
export function useUnitCoverage({
  filterLoading,
  scopeUserId,
  showAllRisks,
}: UseUnitCoverageArgs): UnitCoverageState {
  const [locations, setLocations] = useState<UnitLocationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (filterLoading) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    const user = authService.getCurrentUser();
    const isAdmin = user?.is_admin === true;

    let params: { user_id_no?: number; filter_by_units?: boolean } | undefined;
    if (isAdmin) {
      if (!showAllRisks && scopeUserId != null) {
        params = { user_id_no: scopeUserId };
      }
    } else if (showAllRisks) {
      params = { filter_by_units: false };
    }

    fetchUnitLocations(params)
      .then((rows) => {
        if (!cancelled) setLocations(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setLocations([]);
          setError(err instanceof Error ? err.message : 'Failed to load unit coverage');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filterLoading, scopeUserId, showAllRisks]);

  return { locations, loading, error };
}
